"use client"

import { useRef, useCallback, useEffect, KeyboardEvent } from "react"
import { Send, Square } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/** Mirrors the server DTO's @MaxLength(10000) on the chat message body. */
export const CHAT_INPUT_MAX_LENGTH = 10000

/** Auto-grow ceilings. The old 200px clamp capped the composer at ~7 lines. */
export const CHAT_INPUT_MAX_HEIGHT = 320
export const CHAT_INPUT_COMPACT_MAX_HEIGHT = 180

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop: () => void
  isStreaming: boolean
  disabled?: boolean
  /** PiP variant — smaller chrome, same behaviour (auto-grow, IME, length cap). */
  compact?: boolean
  placeholder?: string
  maxLength?: number
}

export function ChatInput({
  value,
  onChange,
  onSend,
  onStop,
  isStreaming,
  disabled,
  compact,
  placeholder = "Ask anything about your recipients...",
  maxLength = CHAT_INPUT_MAX_LENGTH,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // An IME (CJK, Vietnamese, …) commits a candidate with Enter. Without this
  // guard that Enter reached the send handler and posted half-composed text.
  const composingRef = useRef(false)

  const maxHeight = compact ? CHAT_INPUT_COMPACT_MAX_HEIGHT : CHAT_INPUT_MAX_HEIGHT

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px"
  }, [maxHeight])

  // Keep the box in sync with PROGRAMMATIC value changes, not just typing.
  //
  // adjustHeight ran only from handleChange, so the height tracked keystrokes
  // and nothing else. Every other way `value` moves left it stale: after a send
  // the parent clears the value but a grown, multi-line composer stayed tall
  // over an empty field until the next keypress; a suggested-prompt chip or a
  // restored draft filled it without ever growing it, hiding all but the first
  // line of text the user was about to send.
  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Hard client cap: the server rejects >maxLength with a 400, and a paste
    // can exceed the textarea's own maxLength attribute in some engines.
    onChange(e.target.value.slice(0, maxLength))
    adjustHeight()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey) return

    // keyCode 229 is the legacy "IME is processing" signal; isComposing is the
    // modern one. Browsers disagree on which they emit, so check all three.
    if (
      composingRef.current ||
      (e.nativeEvent as unknown as { isComposing?: boolean }).isComposing ||
      e.keyCode === 229
    ) {
      return
    }

    // While streaming there is nothing to send. Returning WITHOUT
    // preventDefault lets Enter insert a newline instead of being swallowed,
    // so the next message can be drafted while the answer is still arriving.
    if (isStreaming) return

    e.preventDefault()
    if (!value.trim() || disabled) return

    onSend()
    // Reset height after send
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto"
      }
    }, 0)
  }

  const nearLimit = value.length > maxLength * 0.9

  return (
    <div className={cn("border-t border-border bg-background shrink-0", compact ? "p-1.5" : "p-4")}>
      {/* 2026-05-26: rebuilt per user direction — single FULL-WIDTH input
          shell with the send button anchored at the TOP-RIGHT INSIDE the
          textarea container.

          Why not max-w-3xl: user wanted full width across the chat area.
          Why not button-outside: user explicitly asked for button INSIDE
          the input bar (top-right corner). */}
      <div
        className={cn(
          "relative w-full border bg-muted/30 transition-colors",
          compact ? "rounded-lg" : "rounded-xl",
          "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40",
          disabled ? "opacity-50 border-border" : "border-border",
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => { composingRef.current = true }}
          onCompositionEnd={() => { composingRef.current = false }}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          rows={compact ? 2 : 3}
          className={cn(
            "w-full resize-none bg-transparent placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed",
            compact
              ? "px-2.5 py-1.5 pr-10 text-[13px] min-h-[56px] max-h-[180px]"
              : "px-4 py-3 pr-14 text-sm min-h-[104px] max-h-[320px]",
          )}
        />

        {/* Character counter — bottom-left INSIDE the shell, only when typing.
            Placed left so it never sits behind the send button. Turns amber
            near the cap so the truncation is never a silent surprise. */}
        {value.length > 0 && (
          <span
            className={cn(
              "absolute text-[10px] pointer-events-none",
              compact ? "bottom-1 left-2.5" : "bottom-2 left-4",
              nearLimit ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground/50",
            )}
          >
            {nearLimit ? `${value.length} / ${maxLength}` : value.length}
          </span>
        )}

        {/* Send / Stop button — anchored TOP-RIGHT inside the input shell.
            Enabled state is the canonical visual signal the input is ready
            (filled primary background) vs blocked (muted + cursor-not-allowed). */}
        {isStreaming ? (
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "absolute rounded-lg shrink-0",
              compact ? "top-1.5 right-1.5 h-7 w-7" : "top-2 right-2 h-9 w-9",
            )}
            onClick={onStop}
            title="Stop generating"
          >
            <Square className={compact ? "h-3 w-3" : "h-4 w-4"} />
          </Button>
        ) : (
          <Button
            size="icon"
            className={cn(
              "absolute rounded-lg shrink-0",
              compact ? "top-1.5 right-1.5 h-7 w-7" : "top-2 right-2 h-9 w-9",
            )}
            onClick={onSend}
            disabled={!value.trim() || disabled}
            title={value.trim() ? "Send message" : "Type a message to send"}
          >
            <Send className={compact ? "h-3 w-3" : "h-4 w-4"} />
          </Button>
        )}
      </div>

      {!compact && (
        <p className="text-center text-[10px] text-muted-foreground/40 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      )}
    </div>
  )
}
